import { MeasurementQuantityEnum, MeasurementUnitEnum } from 'domains/measurementDomain';
import { MarkupTypeEnum } from './markupDomain';
import { ShipmentTypeEnum } from './orderDomain';
import { CarrierTypeEnum } from './shippingDomain';
import {
  DynamicCustomerFieldDTO,
  InkEnum,
  MultiTypeEnum,
  OrientationEnum,
  SkuPaperOption,
} from './skuDomain';
import { SvcOption, UOMCoefficientEnum } from './svcDomain';
import { ShippingInfo } from './workspaceDetailDomain';
import { JobItem } from './workspaceDomain';

export enum BINDING_EDGE_ENUM {
  'Long Edge' = 'Long Edge',
  'Short Edge' = 'Short Edge',
}

export enum BINDING_CORNER_ENUM {
  'Upper Left' = 'Upper Left',
  'Lower Left' = 'Lower Left',
  'Upper Right' = 'Upper Right',
  'Lower Right' = 'Lower Right',
}

export enum GROMMET_LOCATION_ENUM {
  'None' = 'None',
  'Every 2 Feet' = 'Every 2 Feet',
  'Corners Only' = 'Corners Only',
}

export enum BINDING_TYPE_ENUM {
  'D-Ring' = 'D-Ring',
  'Round Ring' = 'Round Ring',
}

export enum PaperChooseTypeEnum {
  HELP_ME_CHOOSE = 'HELP_ME_CHOOSE',
  SEARCH = 'SEARCH',
  WE_CHOOSE = 'WE_CHOOSE',
}
export enum SizeTypeEnum {
  CUSTOMIZE = 'CUSTOMIZE',
  PRE_SELECTED = 'PRE_SELECTED',
}

export enum SearchScopeEnum {
  ALL_PAPER = 'ALL_PAPER',
  MY_PAPER = 'MY_PAPER',
}

export enum ExclusiveTypeEnum {
  ANY_OF = 'ANY_OF',
  ONE_OF = 'ONE_OF',
  CUSTOMIZE = 'CUSTOMIZE',
}

export enum ServiceOptionTypeEnum {
  RADIO = 'RADIO',
  CHECKBOX = 'CHECKBOX',
  INPUT = 'INPUT',
}

export enum PopoverType {
  shipping = 'shipping',
  shipDate = 'shipDate',
  arrivalDate = 'arrivalDate',
}

export interface IdGroup {
  exclusiveType: ExclusiveTypeEnum;
  idGroups: IdGroup[];
  ids: number[];
}

export interface JobOptionsSvcGroup {
  svcIdGroup: IdGroup;
  svcSpecIdGroup: IdGroup;
  svcTypeIdGroup: IdGroup;
}

export enum PerforateRangeEnum {
  ALL = 'ALL',
  RANGE = 'RANGE',
}

export interface SpecDef {
  label: string;
  parameter: string;
}

export interface Parameters {
  parameters: SpecDef[];
  perforateRange: {
    from: number;
    to: number;
  };
}

export interface JobOptionDef {
  svcId: number;
  typeName: string;
  revxId: number;
  typeId: number;
  revxTypeId: number;
  specDefs: Parameters[];
  options: SvcOption[];
  name: string;
  instructions: string;
  omQuantity: number;
  perforateRangeEnum: PerforateRangeEnum;
  quantityPerBundle: number;
}

export interface JobManualSvc {
  type: string;
  name: string;
  description: string;
  cost: number;
}
export interface JobSpec {
  /** Read-only, already in the viewer's unit. The raw paperBasisWeight is what options match on. */
  paperBasisWeightMetric?: string;
  /**
   * Read-only, already in the viewer's unit. The API sends it alongside the raw field,
   * which stays imperial because selections are matched and posted back on that one.
   */
  finishedSizeMetric?: string;
  flatSizeMetric?: string;

  manualSvcs: JobManualSvc[];
  bindingEdge: BINDING_EDGE_ENUM | BINDING_CORNER_ENUM;
  hasBindingEdge: boolean;
  jobOptions: JobOptionsSvcGroup;
  bleedSizeIn: number;
  bulkAllowance: number;
  coatingBack: string;
  coatingFront: string;
  paperEquivalentCode: number | undefined;
  equipmentCodes: string[];
  exceptPaperIds: number[];
  exceptPressIds: number[];
  exceptSvcIds: number[];
  finishedSizeIn: string;
  /** The ready-made size this job was configured with; see PickedPresetSize. */
  finishedSizePreset?: string;
  finishedSizePresetUnit?: MeasurementUnitEnum;
  flatLengthIn: number;
  flatSizeIn: string;
  flatWidthIn: number;
  forms: number;
  fulfillment: JobFulfillment;
  inkBack: InkEnum;
  inkFront: InkEnum;
  pageCountEditable: boolean;
  isWrapper: boolean;
  multiType: MultiTypeEnum;
  nup: number;
  optionalSvcTypeIds: number[];
  orientation: OrientationEnum;
  pageCount: number;
  pagesPerSide: number;
  paperBasisWeight: string;
  paperBrand: string;
  paperCaliperIn: number;
  paperCategory: string;
  paperChooseType: PaperChooseTypeEnum;
  paperColor: string;
  paperConfigured: boolean;
  paperSearchScope: SearchScopeEnum;
  paperFinish: string;
  paperLengthIn: number;
  paperPackageQty: number;
  paperPriceUom: string;
  paperWeightLb: number;
  paperWidthIn: number;
  paperFixed: boolean;
  parcelCategory: string;
  parts: JobSpec[];
  pmsBack: string[];
  pmsFront: string[];
  quantity: number;
  requiredSvcIds: number[];
  requiredSvcTypeIds: number[];
  sequence: number;
  sheetCount: number;
  sheetSqIn: number;
  shippings: JobShippingSpec[];
  sides: number;
  sizeType: SizeTypeEnum;
  skuId: number;
  skuName: string;
  skuDisplayName: string;
  skuTypeId: number;
  skuTypeName: string;
  svcConfigured: boolean;
  weightBufferLb: number;
  jobOptionDefs: JobOptionDef[];
  jobOption: JobOptionsSvcGroup[];
  additionalJobOptionDefs: JobOptionDef[];
  additionalJobOptions: JobOptionsSvcGroup[];
  requiredSvcGroup: JobOptionsSvcGroup;
  requiredSvcDefs: JobOptionDef[];
  optionalSvcGroup: JobOptionsSvcGroup;
  optionalSvcDefs: JobOptionDef[];
  customPageCountRuleCode: string;
  showSvcFlg: number;
  constraint1Options?: string[];
  constraint2Options?: string[];
  init: boolean;
}

/**
 * A custom size as the two numbers the user typed, in the unit the form is displaying.
 *
 * Sent instead of a joined `finishedSizeIn` string: the request already declares `enteredUnit`, so
 * the API knows which column each side belongs in and fills the other one itself. Joining the
 * string here wrote whatever unit the wizard happened to be showing into the imperial column - a
 * 210x297 typed in metric was stored as 210x297 inches.
 *
 * A pre-selected size still travels as a string, because there it is the SKU's own identity key
 * and is already imperial.
 */
/**
 * The ready-made size a buyer picked, snapshotted onto the job.
 *
 * Two fields because the name alone does not say which catalogue it came from: "Business Card" is
 * in both, at 85x55mm and at 3.5x2in. The name is shown only to a buyer working in that unit.
 */
export interface PickedPresetSize {
  finishedSizePreset?: string;
  finishedSizePresetUnit?: MeasurementUnitEnum;
}

export interface EnteredSize {
  finishedWidth?: number;
  finishedLength?: number;
  flatWidth?: number;
  flatLength?: number;
}

export interface UpdateJobSpecParam
  extends Omit<Partial<JobSpec>, 'parts' | 'svcs'>,
    EnteredSize,
    PickedPresetSize {
  /**
   * The unit the numbers in this request were typed in.
   *
   * Filled from the wizard's selector for every step that posts a measurement without declaring
   * one itself; a step that does declare one - the size step - overrides it.
   */
  enteredUnit?: MeasurementUnitEnum;

  parts?: (Partial<JobSpec> & EnteredSize)[];
  svcs?: Partial<JobSvcFulfillment>[];
  dryRunEnabled?: boolean;
}

export interface JobShippingSpec {
  serialNumber?: number;
  shipmentType: ShipmentTypeEnum;
  accountType?: ShippingAccountTypeEnum;
  carrierType?: CarrierTypeEnum;
  carrierService?: string;
  itemQuantity: number;
  toName?: string;
  toCompany?: string;
  toAddressLine?: string;
  toCity?: string;
  toStateProvinceCode?: string;
  toPostalCode?: string;
  toCountryCode?: string;
  fulfillment: JobShippingFulfillment;
  phoneNumber?: string;
  expectedDeliveryDate?: string;
  orderDate: string;
  isAdd?: boolean;
  internalDistribution?: JobFullfillmentShippingInfoInternalDistribution;
}

export type JobFullfillmentShippingInfoInternalDistribution = {
  quantity?: number | null;
  distribution?: string;
};

export type JobFullfillmentShippingInfo = {
  carrierService: string;
  carrierType: CarrierTypeEnum;
  accountType: ShippingAccountTypeEnum;
  estimatedShipTime?: string;
  estimatedDeliveryTime?: string;
  fromAddressLine: string;
  fromCity: string;
  fromCountryCode: string;
  fromName: string | null;
  fromPostalCode: string;
  fromStateProvinceCode: string;
  shipmentType: ShipmentTypeEnum;
  toAddressLine: string;
  toCity: string;
  toCountryCode: string;
  toName: string;
  toPostalCode: string;
  toStateProvinceCode: string;
  trackingCode: string | null;
  internalDistribution?: JobFullfillmentShippingInfoInternalDistribution;
};

export type JobShippingFulfillment = {
  freeShipping: boolean;
  freightCharge: number;
  orgId: number;
  parcelCategory: string | null;
  parcelCost: number;
  parcelCount: number;
  parcelHeightIn: number;
  parcelId: number;
  parcelLengthIn: number;
  parcelMarkup: number;
  parcelMarkupPct: number;
  parcelMarkupType: string;
  parcelName: string;
  parcelOwnMarkupPct: number;
  parcelVersion: string;
  parcelWidthIn: number;
  printingCharge: number;
  revshopShippingMarkup: number;
  revshopShippingMarkupPct: number;
  revshopShippingMarkupType: string;
  shippingCost: number;
  shippingMarkup: number;
  shippingMarkupPct: number;
  shippingMarkupType: string;
  shippingOwnMarkupPct: number;
  shippingWeight: number;
  supplierFreightCharge: number;
  supplierPrintingCharge: number;
  supplierTotalCharge: number;
  totalCharge: number;
  shippingInfo: JobFullfillmentShippingInfo;
  rush?: boolean;
  tbd?: boolean;
  suggestReadyDate: string;
  readyDate: string;
};

export interface JobFulfillmentPaper {
  /** Metric twin, sent alongside so switching units needs no refetch. */
  paperBasisWeightMetric?: string;

  /** Metric twin, sent alongside so switching units needs no refetch. */
  paperWidthMm?: number;
  paperLengthMm?: number;

  paperId: number;
  paperName: string;
  paperCost: number;
  paperOwnMarkupPct: number;
  paperMarkupType: MarkupTypeEnum;
  paperMarkupPct: number;
  paperMarkup: number;
  paperWeight: string;
  paperCharge: number;
  paperLengthIn: number;
  paperWidthIn: number;
  paperWeightLb: number;
  paperFinish: string;
  paperCategory: string;
  paperBasisWeight: string;
  paperColor: string;
  paperConstraint1: string;
  paperConstraint2: string;
  paperSearchScope: string;
}

export interface JobFulfillment extends JobFulfillmentPaper {
  orgId: number;
  pressId: number;
  pressName: string;
  frontClick: number;
  backClick: number;
  pressCost: number;
  pressOwnMarkupPct: number;
  pressMarkupType: MarkupTypeEnum;
  pressMarkupPct: number;
  pressMarkup: number;
  pressCharge: number;
  globalMarkupType: MarkupTypeEnum;
  globalMarkupPct: number;
  globalMarkup: number;
  revshopMarkupType: MarkupTypeEnum;
  revshopMarkupPct: number;
  revshopMarkup: number;
  freightCharge: number;
  printingCharge: number;
  totalCharge: number;
  supplierFreightCharge: number;
  supplierPrintingCharge: number;
  supplierTotalCharge: number;
  pressRunTimeExpression: PressRunTimeExpression;
  jobSvcFulfillments: JobSvcFulfillment[];
  freeShipping: boolean;
  estimatedPrintingEndTime: string;
}

export interface PressRunTimeExpression {}

// export interface JobSvcSpec {
//   svcTypeId: number;
//   baseSvcSpecDescription: string;
//   typeName: string;
//   baseSvcId: number;
//   baseSvcDescription: string;
//   maxTotalCaliperIn: number;
//   quantityPerBundle: null;
//   options: JobSvcOption[];
//   fulfillment: JobSvcFulfillment;
// }

export interface JobSvcFulfillment {
  orgId: number;
  svcId: number;
  svcName: string;
  specId: number;
  specDescription: string;
  typeId: number;
  typeName: string;
  options: JobSvcOption[];
  maxTotalCaliperIn: number;
  materialThicknessIn: number;
  quantityPerBundle: number;
  perforateRange: {
    from: number;
    to: number;
  };
  perforateRangeEnum: PerforateRangeEnum;
  volumeNumber: number;
  instructions: string;
  svcCost: number;
  svcOwnMarkupPct: number;
  svcMarkupPct: number;
  svcMarkupType: MarkupTypeEnum;
  svcMarkup: number;
  materialCost: number;
  materialOwnMarkupPct: number;
  materialMarkupPct: number;
  materialMarkupType: MarkupTypeEnum;
  materialMarkup: number;
  // svcRunTimeExpression: SVCRunTimeExpression;
}

export interface QuantityItem {
  quantity: number;
  selected: boolean;
  serialNumber: number;
}
export interface StartJobConfigParam {
  skuId: number;
  quantity: number;
  isExpert: boolean;
  directlyAddToCart?: boolean;
}

export interface JobVersion {
  name: string;
  pct: number;
  quantity: number;
}
export interface JobSpecItem {
  quantity: number;
  selected: boolean;
  serialNumber: number;
  spec: JobSpec;
  versions: JobVersion[];
}

export enum PROOF_TYPE_ENUM {
  DIGITAL = 'DIGITAL',
  HARD_COPY = 'HARD_COPY',
}

export enum JOB_CONFIG_STATUS_ENUM {
  DRAFT = 'DRAFT',
  QUOTE = 'QUOTE',
}

export enum RFQOrgStatusEnum {
  INIT = 'INIT',
  DRAFT = 'DRAFT',
  TO_REVIEW = 'TO_REVIEW',
  IN_PROCESS = 'IN_PROCESS',
  DECLINED = 'DECLINED',
  NOT_AWARDED = 'NOT_AWARDED',
  AWARDED = 'AWARDED',
}

export enum RFQStatusEnum {
  IDENTIFY_SUPPLIER = 'IDENTIFY_SUPPLIER',
  RFQ_PROCESS = 'RFQ_PROCESS',
  COMPLETE = 'COMPLETE',
  CLOSED = 'CLOSED',
}

export enum ShippingAccountTypeEnum {
  REVSHOP = 'REVSHOP',
  ORGANIZATION = 'ORGANIZATION',
  SUPPLIER = 'SUPPLIER',
}

export interface JobSharedItem {
  email: string;
  firstName: string;
  lastName: string;
}

export interface JobConfigFileItem {
  createAt: string;
  creatorId: number;
  fileName: string;
  fileUrl: string;
  id: string;
  skuId: number;
  needHighResolution?: boolean;
}
export interface JobConfig {
  id: string;
  jobId: number;
  jobProjectId: string;
  revisionId: number;
  artFiles: JobConfigFileItem[];
  isDraft: boolean;
  canAddToCart: boolean;
  shippingNote: string;
  remark: string;
  orderDate: string;
  proofType: PROOF_TYPE_ENUM;
  status: JOB_CONFIG_STATUS_ENUM;
  proofContactAddressId: number;
  proofContactId: number;
  projectName: string;
  isProxied: boolean;
  specItems: JobSpecItem[];
  jobSharedWiths: JobSharedItem[];
  digitalProofRequired: boolean;
  digitalProofOptional: boolean;
  hardcopyProofRequired: boolean;
  skipArtFile: boolean;
  hasAdjustment: boolean | null;
  internalDistributionEnabled: boolean;
  /**
   * PRD-471: true when Industry Average fulfills this job config and the network admin disabled
   * Add to Cart for it - the UI offers a Contact Revshop button instead.
   */
  industryAverageAddToCartBlocked?: boolean;
  proofEditable: boolean;
  jobDynamicInfo?: JobDynamicInfoDTO;
  frontTemplateId?: number;
  backTemplateId?: number;
  /** True when job was created from Canva Content Publisher publish (server-set). */
  canvaContentPublisher?: boolean;
  /**
   * No size on this SKU can hold the design brought from Canva. Shows the size section instead
   * of hiding it, prefills a custom-size SKU with the design's dimensions, and warns on one that
   * cannot take a custom size.
   */
  canvaSizeMismatch?: boolean;
  /** Trim size of the Canva design, in inches — what a custom size is prefilled with. */
  canvaDesignWidthIn?: number;
  canvaDesignLengthIn?: number;
  /**
   * Expert UI step types to hide (matches {@link StepTypeEnum} names). Set at job start for Canva
   * publish; sequential step numbers skip hidden sections.
   */
  expertHiddenSteps?: string[];
}

export interface JobDynamicInfoDTO {
  approve: boolean;
  saveParams: Record<string, string>;
  frontPdfUrl: string;
  endPdfUrl: string;
  endImageUrl?: string;
  customerFields: DynamicCustomerFieldDTO[];
}

export interface JobConfigViewer {
  /**
   * Read-only, already in the viewer's unit. The API sends it alongside the raw field,
   * which stays imperial because selections are matched and posted back on that one.
   */
  finishedSizeMetric?: string;

  description: string;
  finishedSizeIn: string;
  id: string;
  image: string;
  jobSharedWiths: JobSharedItem[];
  jobType: string;
  /** Always imperial; `paperSummariesMetric` carries the gsm form. Read-only. */
  paperSummaries: string[];
  /** The gsm form, sent alongside so switching units needs no refetch. Read-only. */
  paperSummariesMetric?: string[];
  quantity: number;
  skuDisplayName: string;
  skuId: number;
  skuName: string;
  svcSummaries: string[];
  addSharedWith?: JobSharedItem;
}

export enum JobGenerateTypeEnum {
  JOB_CONFIG = 'JOB_CONFIG',
  JOB_RFQ = 'JOB_RFQ',
  JOB_ESTIMATOR = 'JOB_ESTIMATOR',
}

export enum JobConfigGenerateTypeEnum {
  NORMAL = 'NORMAL',
  EXPERT = 'EXPERT',
}

export enum JobConfigSourceEnum {
  SHOP = 'SHOP',
  GALLERY = 'GALLERY',
}

export interface JobSvc {
  description: string;
  id: number;
  materialLengthIn: number;
  materialThicknessIn: number;
  materialUom: number;
  materialUomCoefficient: UOMCoefficientEnum;
  materialWidthIn: number;
  maxTotalCaliperIn: number;
  options: JobSvcOption[];
  uom: number;
  uomCoefficient: UOMCoefficientEnum;
}

export interface UpdateJobConfigVersion {
  jobConfigId: string;
  versionsItems: {
    serialNumber: number;
    versions: {
      name: string;
      pct: number;
      quantity: number;
    }[];
  }[];
}

export interface QuantityPayload {
  id: string;
  quantity: number;
  serialNumber: number;
}

export interface OptionPapers {
  jobConfigId: string;
  searchScope: SearchScopeEnum;
  targetSkuId: number;
}

export interface OptionPapersList {
  /** Metric twin, sent alongside so switching units needs no refetch. */
  basisWeightMetric?: string;

  /** Metric twin, sent alongside so switching units needs no refetch. */
  widthMm?: number;
  lengthMm?: number;

  basisWeight: string;
  brand: string;
  category: string;
  color: string;
  finish: string;
  id: number;
  name: string;
  constraint1: string;
  constraint2: string;
  widthIn: number;
  lengthIn: number;
}

export interface OptionPaperCharges {
  jobConfigId: string;
  // paperIds: number[];
  papers: OptionPapersList[];
  searchScope: SearchScopeEnum;
  serialNumbers: number[];
  targetSkuId: number;
}

export interface JobSvcOption {
  isWrapper: boolean;
  jobConfigId: string;
  targetSkuId: number;
  data?: JobConfigSvcOptionType;
}

export interface OptionPaperChargesList {
  chargeItems: {
    charge: number;
    quantity: number;
    serialNumber: number;
  }[];
  paper: JobFulfillmentPaper;
}

export interface JobConfigShippingDate {
  calendarDate: string;
  selected: boolean;
  services: ShippingCalendarDateService[];
  rush: boolean;
}

export interface JobConfigShippingCalendar {
  dates: JobConfigShippingDate[];
  shippingItemSerialNumber: number;
}

export interface JobConfigQuantityItemShippingCalendar {
  quantity: number;
  quantityItemSerialNumber: number;
  shippingCalendars: JobConfigShippingCalendar[];
}

export interface ShippingCalendarDateService {
  accountType: ShippingAccountTypeEnum;
  carrierService: string;
  carrierType: CarrierTypeEnum;
  deliveryDateTime: string;
  estimatedShipTime: string;
  selected: boolean;
  shipmentType: ShipmentTypeEnum;
  shippingCost: number;
  shippingTotalCharge: number;
  shippingPrice: number;
  rush: boolean;
  tbd: boolean;
  freeShipping: boolean;
}

export interface JobOptionSvcOptions {
  /**
   * Each value mapped to its label in the viewer's unit, sent only for options tagged as
   * measurements. The raw value stays the one submitted and matched on.
   */
  valuesMetric?: Record<string, string> | null;
  label: string;
  values: string[];
  isRequired: boolean;
  type: ServiceOptionTypeEnum;
  defaultValue?: string;
  /**
   * What this option's values measure, if anything. Absent on options saved before the field
   * existed, which is treated as NONE - shown exactly as entered.
   */
  valueUom?: MeasurementQuantityEnum;
}

export interface JobOptionSvcSpecParameters {
  label: string;
  parameter: string;
}

export interface JobOptionSvcSpec {
  parameters: JobOptionSvcSpecParameters[];
  id: number;
  revxId: number;
  description: string;
}

export interface JobOptionSvc {
  typeId: number;
  revxTypeId: number;
  typeName: string;
  id: number;
  revxId: number;
  name: string;
  showInstructions: boolean;
  message: string;
  options: JobOptionSvcOptions[];
  specs: JobOptionSvcSpec[];
  parameterLabels: string[];
  isRequired: boolean;
  svcs: JobSvc[];
  typeCategory: string;
  groupsId?: number[];
  uomCoefficient: UOMCoefficientEnum;
}

export interface JobOptionSvcGroup {
  isRequired: boolean;
  exclusiveType: ExclusiveTypeEnum;
  typeId: number;
  svcs: JobOptionSvc[];
  svcGroups: JobOptionSvcGroup[];
}

export interface JobConfigSvcOptionType {
  requiredSvcGroup: JobOptionSvcGroup[];
  optionalSvcGroup: JobOptionSvcGroup[];
  jobOptions: JobOptionSvcGroup[];
  additionalJobOptions: JobOptionSvcGroup[];
}

export interface CreateShippingParam {
  jobConfigId: string;
  orderDate: string;
  serialNumber: number;
  toAddressLine?: string;
  toCity?: string;
  toCountryCode?: string;
  toName?: string;
  toCompany?: string;
  toPostalCode?: string;
  toStateProvinceCode?: string;
}

export interface UpdateShippingParam {
  jobConfigId: string;
  note?: string;
  orderDate?: string;
  quantityItems?: {
    serialNumber: number;
    shippingItems: Partial<JobShippingSpec>[];
  }[];
}

export interface SearchCalendarParam {
  jobConfigId: string;
  queryQuantity: number;
  serialNumber: number;
  toAddressLine: string;
  toCity: string;
  toCompany: string;
  toCountryCode: string;
  toName: string;
  toPostalCode: string;
  toStateProvinceCode: string;
}

// expression
export interface JobSpecItemExpression {
  quantity: number;
  serialNumber: number;
  costExpression: Expression;
  timeExpression: Expression;
}

export interface Expression {
  name: string;
  node: ExpressionNode;
  precision: number;
  result: number;
  roundMode: string;
  toString: string;
}

export interface ExpressionNode {
  bracketNum: number;
  expression: Expression | null;
  name: string;
  next: ExpressionNode | null;
  operator: ExpressionOperatorEnum;
  value: number;
}

export enum ExpressionOperatorEnum {
  add = 'ADD',
  sub = 'SUB',
  div = 'DIV',
  mul = 'MUL',
  max = 'MAX',
  min = 'MIN',
}

export enum EstimatorTypeEnum {
  JOB_ESTIMATOR = 'JOB_ESTIMATOR',
  SUPPLIER_COMPARATOR = 'SUPPLIER_COMPARATOR',
}

export interface MaterialsBasePaperOption {
  basisWeights: string[];
  brands: string[];
  categories: string[];
  colors: string[];
  companies: string[];
  finishes: string[];
  paperIds: string[];
  subCategories: string[];
}

export interface CompareWithOrgParam {
  jobConfigId: string;
  orgId?: number;
  serialNumber: number;
}

export interface AddSharedParam {
  jobConfigId: string | number;
  email: string;
  firstName: string;
  lastName: string;
  serialNumbers: number[];
}

export interface DeleteSharedParam {
  jobConfigId: string;
  email: string;
}

export interface SendSharedListParam {
  jobConfigId: string;
  emails?: string[];
  serialNumbers: number[];
}

export interface ChangeJobNameParam {
  jobConfigId: string;
  name: string;
}

export interface BasicUpdateParam {
  jobSharedWiths: {
    email: string;
    firstName: string;
    lastName: string;
  }[];
  orderDate: string;
  projectName: string;
  proofType: PROOF_TYPE_ENUM;
  remark: string;
  shippingNote: string;
  proofContactAddressId: number;
  proofContactId: number;
  digitalProofRequired: boolean;
}

export interface JobEstimator {
  estimatorType: EstimatorTypeEnum;
  quantityItems: JobEstimatorQuantityItem[];
  jobConfigId: string;
  digitalProofRequired: boolean;
  hardcopyProofRequired: boolean;
}

export interface JobEstimatorQuantityItem {
  serialNumber: number;
  quantity: number;
  comparatorItems: JobEstimatorComparatorItem[];
  rush: boolean;
  averageEligible: boolean;
  revshopEligible: boolean;
}

export interface JobEstimatorComparatorItem {
  orgId: number;
  orgName: string;
  serialNumber: number;
  comparator: JobComparator;
  eligible: boolean;
  priceAdjusted: boolean;
  averageEligible: boolean;
  revshopEligible: boolean;
  boxChargeEditable: boolean;
  /**
   * PRD-471: the column is priced by the Industry Average org and the network admin has Add to Cart
   * blocked for it. Unlike `configItem.industryAverageAddToCartBlocked` this follows the org the
   * sourcer picks per column, so it is already true before the job config knows about the choice.
   */
  addToCartDisabled: boolean;
  optionOrgs: {
    eligible: boolean;
    orgId: number;
    orgName: string;
  }[];
}

export interface JobComparator {
  // basic
  skuId: number;
  skuName: string;
  orgId: number;

  // svc
  svcs: JobComparatorSvc[];

  // parts
  parts: JobComparatorPart[];

  // shipping
  shippings: JobComparatorShipping[];

  // parcel
  parcel: JobComparatorParcel;

  ///markup
  supplierMarkup: number;
  supplierShippingMarkup: number;
  supplierMarkupPct: number;
  supplierShippingMarkupPct: number;

  revxMarkup: number;
  revxShippingMarkup: number;
  revxMarkupPct: number;
  revxShippingMarkupPct: number;

  outsourceMarkup: number;
  outsourceShippingMarkup: number;
  outsourceMarkupPct: number;
  outsourceShippingMarkupPct: number;

  supplierTotalCharge: number;
  freightCost: number;
  printingCost: number;

  //charge
  globalMarkup: number;
  supplierFreightCharge: number;
  supplierPrintingCharge: number;
  revxFreightCharge: number;
  revxPrintingCharge: number;
  printingCharge: number;
  freightCharge: number;
  totalCharge: number;
  revxTotalCharge: number;

  rush: boolean;
  extraSupplierShippingCharge: number;
  extraSupplierShippingCost: number;
  extraSupplierShippingMarkup: number;
  extraSupplierShippingMarkupPct: number;

}

export interface JobRFQViewer {
  id: number;
  projectName: string;
  skuName: string;
  size: string;
  orientation: OrientationEnum;
  filesCount: number;
  quantities: number[];
  cacheId: string;
  rfqStatus: RFQStatusEnum;
  orgItems: JobRFQOrgItemViewer[];
  orgId: number;
}

export interface JobRFQSupplierResponseRecordView {
  rfqId: number | null;
  supplierOrgId: number | null;
  supplierOrgName: string | null;
  id: number | null;
  qtySerialNumber: number | null;
  quantity: number | null;
  freeShipping: boolean | null;
  freightCost: number | null;
  printingCost: number | null;
  responseTime: Date | null;
  supplierShippingMarkup: number | null;
  supplierPrintingMarkup: number | null;
  supplierShippingMarkupPct: number | null;
  supplierPrintingMarkupPct: number | null;
  supplierPrintingCharge: number | null;
  supplierFreightCharge: number | null;
  revxShippingMarkup: number | null;
  revxPrintingMarkup: number | null;
  revxShippingMarkupPct: number | null;
  revxPrintingMarkupPct: number | null;
  revxPrintingCharge: number | null;
  revxFreightCharge: number | null;
  revxFreightCost: number | null;
  supplierCharge: number | null;
  revxCharge: number | null;
  shippingItems: JobRFQDraftShippingItemsViewer[] | null;
  qtyItemRequirement: QtyItemRequirement | null;
  estimateTurnaroundDays: number | null;
  readyDate: string | null; // ISO date string "yyyy-MM-dd"
  revisionNumber: number | null;
  supplierNote: string | null;
  extraSupplierShippingCharge: number | null;
  extraSupplierShippingCost: number | null;
  extraSupplierShippingMarkup: number | null;
  extraSupplierShippingMarkupPct: number | null;
}

export interface JobRFQDraftViewer {
  orgItems: JobRFQDraftOrgItemsViewer[];
}

export interface JobRFQDraftOrgItemsViewer {
  qtyItems: JobRFQDraftQtyItemsViewer[];
  orgId: number;
  orgName: string;
  fromName: string;
  fromAddressLine: string;
  fromCity: string;
  fromStateProvinceCode: string;
  fromPostalCode: string;
  fromCountryCode: string;
  recipients: JobRFQRecipientViewer[];
  currentSelectIndex: number;
  isResent: boolean;
  draftFromUpdateShipping: boolean;
  sourcerNote: string;
  rfqOrgStatus: RFQOrgStatusEnum;
}

export interface JobRFQDraftQtyItemsViewer {
  qtySerialNumber: number;
  quantity: number;
  shippingItems: JobRFQDraftShippingItemsViewer[];
  viewed: boolean;
  manualOverrideReadyDate: boolean;
  recommendShippingPrice: number;
}

export interface JobRFQDraftShippingItemsViewer {
  orgId: number;
  orgName: string;
  qtySerialNumber: number;
  shippingSerialNumber: number;
  quantity: number;
  toName: string;
  toAddressLine: string;
  toCity: string;
  toStateProvinceCode: string;
  toPostalCode: string;
  toCountryCode: string;
  readyDate: string;
  deliveryDate: string;
  originalDeliveryDate: string;
  overrideDeliveryDate: string;
  expectedDeliveryDate: string;
  carrierType: CarrierTypeEnum;
  shipmentType: ShipmentTypeEnum;
  carrierService: string;
  accountType: ShippingAccountTypeEnum;
  accountTypes: ShippingAccountTypeEnum[];
  price: number;
  carrierServiceSelects: CarrierServiceSelects[];
  accountTypeSelects: JobRFQDraftShippingItemsAccountSelect[];
  tbd: boolean;
  deliveryDateExceededAck: boolean;
  deliveryDateExceeded: boolean;
  shippingMethodAllowed: boolean;
}

export interface JobRFQDraftShippingItemsAccountSelect {
  accountType: ShippingAccountTypeEnum;
  company: string;
  orgId: number;
  orgName: string;
}

export interface CarrierServiceSelects {
  accountType: ShippingAccountTypeEnum;
  businessDaysInTransit: string;
  carrierType: CarrierTypeEnum;
  charge: number;
  dayOfWeek: string;
  estimatedDeliveryTime: string;
  estimatedShipTime: string;
  rush: boolean;
  serviceName: string;
  shipmentType: string;
  shippingAccount: string;
  tbd: boolean;
}

export interface JobRFQDraftShippingAdjustParams {
  serialNumber: number;
  accountType?: ShippingAccountTypeEnum;
  readyDate?: string;
  service?: string;
  charge?: number;
  deliveryDate?: string;
  deliveryDateExceededAck?: boolean;
  sourcerNote?: string;
  carrierType?: CarrierTypeEnum;
  manualOverrideReadyDate?: boolean;
}

export interface QtyItemRequirement {
  serialNumber: number;
  quantity: number;
  shippingItems: ShippingItemRequirement[];
}

export interface ShippingItemRequirement {
  serialNumber: number;
  deliveryDate: string;
  carrierService: string;
}

export interface JobRFQResponseViewer {
  jobRFQ: JobItem;
  comparatorItems: JobRFQComparatorItemViewer[];
  orgStatus: RFQOrgStatusEnum;
  isEligibleOrg: boolean;
  orgId: number;
  rush: boolean;
  digitalProofRequired: boolean;
  hardcopyProofRequired: boolean;
  supplierNote: string;
  sourcerNote: string;
}

export interface JobRFQComparatorItemViewer {
  serialNumber: number;
  quantity: number;
  comparator: JobComparator;
  revisionHistory?: JobRFQQtyItemRevisionViewer[];
  orgStatus: RFQOrgStatusEnum;
  optionOrgs: {
    eligible: boolean;
    orgId: number;
    orgName: string;
  }[];
}

export interface JobRFQOrgItemViewer {
  id: number;
  rfqId: number;
  supplierOrgId: number;
  supplierOrgName: string;
  supplierCompanyId: number;
  supplierCompanyName: string;
  qtyItems: JobRFQQtyItemViewer[];
  recipients: JobRFQRecipientViewer[];
  sendAt: string;
  declineAt: string;
  awardedAt: string;
  responseBy: string;
  responseAt: string;
  rfqOrgStatus: RFQOrgStatusEnum;
  qtyItemRequirement: QtyItemRequirement[];
  estimateTurnaroundDays: number;
  readyDate: string;
  isResent: boolean;
  suggestReadyDate: string;
  digitalProofRequired: boolean;
  hardcopyProofRequired: boolean;
  hasUpdateShippingDraft: boolean;
  supplierNote: string;
  sourcerNote: string;
  isConfirmation: boolean;
  supplierFiles: JobConfigFileItem[];
}

export interface JobRFQQtyItemViewer {
  id: number;
  qtySerialNumber: number;
  quantity: number;
  rfqOrgStatus: RFQOrgStatusEnum;
  supplierPrintingCharge: number;
  supplierShippingCharge: number;
  extraSupplierShippingCharge: number;
  supplierCharge: number;
  revxPrintingCharge: number;
  revxShippingCharge: number;
  revxCharge: number;
  readyDate: string;
  shippingItems: JobRFQShippingItemViewer[];
  supplierFreightCharge: number;
  revxShippingMarkup: number;
  revxShippingMarkupPct: number;
  revxPrintingMarkup: number;
  revxPrintingMarkupPct: number;
  revxFreightCharge: number;
  revxFreightCost: number;
  supplierCompanyName: string;
  revisionHistory?: JobRFQQtyItemRevisionViewer[];
  revisionNumber?: number;
}

export interface JobRFQQtyItemRevisionViewer {
  id: number;
  qtySerialNumber: number;
  quantity: number;
  rfqOrgStatus: RFQOrgStatusEnum;
  supplierPrintingCharge: number;
  supplierShippingCharge: number;
  extraSupplierShippingCharge: number;
  supplierCharge: number;
  revxPrintingCharge: number;
  revxShippingCharge: number;
  revxCharge: number;
  readyDate: string;
  shippingItems: JobRFQShippingItemViewer[];
  supplierFreightCharge: number;
  revxShippingMarkup: number;
  revxShippingMarkupPct: number;
  revxPrintingMarkup: number;
  revxPrintingMarkupPct: number;
  supplierPrintingMarkup: number;
  supplierPrintingMarkupPct: number;
  revxFreightCharge: number;
  revxFreightCost: number;
  supplierCompanyName: string;
  revisionNumber?: number;
  supplierNote?: string;
  supplierFiles: JobConfigFileItem[];
}

export interface JobRFQShippingItemViewer {
  serialNumber: number;
  quantity: number;
  carrierType: CarrierTypeEnum;
  accountType: ShippingAccountTypeEnum;
  businessDaysInTransit?: number;
  shippingInfo: ShippingInfo;
  estimatedDeliveryTime: string;
  estimatedShipTime: string;
  supplierShippingCharge: number;
  revxShippingCharge: number;
  revxFreightCharge: number;
  supplierFreightCharge: number;
  freightCost: number;
  shippingCost: number;
  suggestReadyDate: string;
  readyDate: string;
  expectedDeliveryDate: string;
  orderDate: string;
  revxFreightCost: number;
  originalShipDate: string;
  adjustShipDate: string;
  shippingMethodAllowed: boolean;
  deliveryDate: string;
}
export interface FlushHighResFileREsponseDTO {
  success: boolean;
  code: number;
  message: string;
}

export interface JobRFQRecipientViewer {
  userId: number;
  firstName: string;
  lastName: string;
  checked: boolean;
}

export interface JobRFQQuoteItemViewer {
  qtySerialNumber: number;
  quantity: number;
  orgId: number;
  orgName: string;
  supplierPrintingCharge: number;
  supplierFreightCharge: number;
  revxPrintingCharge: number;
  revxPrintingMarkup: number;
  revxPrintingMarkupPct: number;
  revxFreightCharge: number;
  revxShippingMarkup: number;
  revxShippingMarkupPct: number;
  revxCharge: number;
  shippingItems: JobRFQShippingItemViewer[];
  shippingInfos: ShippingInfo[];
  revxPrintingCost: number;
  revxFreightCost: number;
  companyName: string;
  readyDate: string;
  outsourcePrintingMarkup: number;
  outsourcePrintingMarkupPct: number;
  outsourceShippingMarkup: number;
  outsourceShippingMarkupPct: number;
  outsourceFreightCharge: number;
  outsourcePrintingCharge: number;
  outsourceCharge: number;
  extraSupplierShippingCharge: number;
  extraSupplierShippingCost: number;
  extraSupplierShippingMarkup: number;
  extraSupplierShippingMarkupPct: number;
}

export interface GenerateInvoiceResponse {
  fileName?: string;
  path?: string;
  //1 QuickBooks 2: Revshop
  invoiceType?: number;
  invoiceNumber?: string;
}

export interface JobRFQSendOrgItemsParams {
  orgId: number;
  serialNumbers: number[];
}

export interface JobRFQSendDraftOrgItemsParams {
  orgId: number;
  serialNumbers: number[];
  recipients: number[];
}

export interface JobRFQBuildQuoteItemsParams {
  orgId: number;
  qtySerialNumber: number;
}

export interface JobRFQSwitchShippingAccountParams {
  accountType: ShippingAccountTypeEnum;
  orgId: number;
  qtySerialNumber: number;
  rfqId: number;
}

export interface JobRFQShippingAccount {
  serialNumber: number;
  services: JobRFQShippingAccountServices[];
}

export interface JobRFQShippingAccountServices {
  accountType: ShippingAccountTypeEnum;
  businessDaysInTransit: string;
  carrierType: CarrierTypeEnum;
  supplierCost: number;
  supplierCharge: number;
  dayOfWeek: string;
  estimatedDeliveryTime: string;
  estimatedShipTime: string;
  selected: boolean;
  serviceName: string;
  shipmentType: ShipmentTypeEnum;
  rush: boolean;
}

export interface AdjustMarkupPayload {
  jobConfigId: string;
  quantityItemSerialNumber: number | string;
  comparatorItemSerialNumber: number | string;
  revxMarkup?: number | string;
  revxMarkupPct?: number | string;
  revxShippingMarkup?: number | string;
  revxShippingMarkupPct?: number | string;
  outsourceMarkup?: number | string;
  outsourceMarkupPct?: number | string;
  outsourceShippingMarkup?: number | string;
  outsourceShippingMarkupPct?: number | string;
  supplierMarkupPct?: number | string;
  supplierMarkup?: number | string;
  supplierFreightCharge?: number | string;
  supplierPrintingCharge?: number | string;
  readyDate?: string;
  shippingAdjusts?: Partial<JobShippingAdjust[]>;
  parcelCost?: number;
  paperCharge?: number;
}

export interface AdjustPressPayload {
  jobConfigId: string;
  quantityItemSerialNumber: number;
  comparatorItemSerialNumber: number;
  pressAdjusts: {
    skuId: number;
    revxPressId: number;
  }[];
}

export interface ChangeOrgPayload {
  jobConfigId: string;
  orgId: number;
  quantityItemSerialNumber: number;
  comparatorItemSerialNumber: number;
}

export interface JobComparatorPart {
  // sku
  skuId: number;
  skuName: string;
  pageCount: number;

  // paper
  paperId: number;
  /** Always in pounds; `paperWeightMetric` carries the gsm form. Read-only. */
  paperWeight: string;
  /** The gsm form, sent alongside so switching units needs no refetch. Read-only. */
  paperWeightMetric?: string;
  paperCategory: string;
  paperName: string;
  paperCharge: number;

  // press
  pressId: number;
  revxPressId: number;
  pressName: string;
  pressCode: string;
  pressCharge: number;
  optionPresses: JobOptionPress[];

  // svc
  svcs: JobComparatorSvc[];
}

export interface JobComparatorShippingCarrierServices {
  carrierType: CarrierTypeEnum;
  charge: number;
  dayOfWeek: string;
  estimatedDeliveryTime: string;
  estimatedShipTime: string;
  serviceName: string;
  shipmentType: ShipmentTypeEnum;
}

export interface JobComparatorShipping {
  serialNumber: number;
  quantity: number;
  carrierType: CarrierTypeEnum;
  accountType: ShippingAccountTypeEnum;
  businessDaysInTransit?: number;
  freeShipping?: boolean;
  shippingInfo: ShippingInfo;
  estimatedDeliveryTime: string;
  estimatedShipTime: string;
  supplierShippingCharge: number;
  revxShippingCharge: number;
  revxFreightCharge: number;
  supplierFreightCharge: number;
  freightCost: number;
  shippingCost: number;
  suggestReadyDate: string;
  readyDate: string;
  expectedDeliveryDate: string;
  orderDate: string;
  revxFreightCost: number;
  originalShipDate: string;
  adjustShipDate: string;
  shippingMethodAllowed: boolean;
  deliveryDate: string;
}

export interface JobComparatorSvc {
  name: string;
  charge: number;
}

export interface JobComparatorParcel {
  /** Always in inches; `dimensionsMetric` carries the millimetre form. Read-only. */
  dimensions: string[];
  /** The millimetre form, sent alongside so switching units needs no refetch. Read-only. */
  dimensionsMetric?: string[];
  charge: number;
  oldPaperCharge?: number;
}

export interface JobOptionPress {
  id: number;
  name: string;
  componentCode: string;
  componentCodeName: string;
  manufacturingTypeDescription: string;
  pressTypeDescription: string;
}

export interface JobShippingAdjust {
  serialNumber?: number;
  charge?: number;
  accountType?: ShippingAccountTypeEnum;
}

export interface JobRFQAdjustParams {
  markupAdjust: Partial<JobPriceAdjust>;
  orgId: number;
  readyDate?: string;
  pressAdjusts: Partial<JobPartPressAdjust[]>;
  shippingAdjusts: Partial<JobShippingAdjust[]>;
  supplierNote?: string;
}

export interface JobPriceAdjust {
  printingCost: number;
  freightCost: number;
  supplierMarkup: number;
  supplierMarkupPct: number;
  supplierShippingMarkup: number;
  supplierShippingMarkupPct: number;
  revxMarkup: number;
  revxMarkupPct: number;
  revxShippingMarkup: number;
  revxShippingMarkupPct: number;
  outsourceMarkup: number;
  outsourceMarkupPct: number;
  outsourceShippingMarkup: number;
  outsourceShippingMarkupPct: number;
  charge: number;
  readyDate: string;
  revxPrintingCost: number;
  revxFreightCost: number;
  extraSupplierShippingCost?: number;
  extraSupplierShippingMarkup?: number;
  extraSupplierShippingMarkupPct?: number;
}

export interface JobPartPressAdjust {
  skuId: number;
  revxPressId: number;
}

export interface JobRFQAdjustPayload {
  id: number | string;
  orgId: number;
  qtySerialNumber: number;
  comparatorItemSerialNumber?: number;
  body: Partial<JobRFQAdjustParams>;
}

export interface ArtworkFileParams {
  skuId: number;
  jobId: number;
  fileUrl: string;
  fileName: string;
}

export interface OverridePricePayload {
  body: Partial<JobPriceAdjust>;
  jobConfigId: string;
  quantityItemSerialNumber: number;
  comparatorItemSerialNumber: number;
}

export interface PlatformOptionPaper {
  /** Metric twin, sent alongside so switching units needs no refetch. */
  basisWeightMetric?: string;

  /** Metric twin, sent alongside so switching units needs no refetch. */
  widthMm?: number;
  lengthMm?: number;

  equivalentCode: string;
  basisWeight: string;
  brand: string;
  category: string;
  color: string;
  finish: string;
  id: number;
  name: string;
  constraint1: string;
  constraint2: string;
  widthIn: number;
  lengthIn: number;
}

export interface PlatformOptionPaperScope {
  allPapers: PlatformOptionPaper[];
  myPapers: PlatformOptionPaper[];
}

export interface PlatformSkuPaperUpdateParams {
  id: number;
  version: number;
  skuPaperOptions: SkuPaperOption;
  skipPaperOptions: boolean;
  hidePaperConstraintSection: boolean;
  hidePaperSearchSection: boolean;
  paperEquivalentCode: string;
  paperSearchScope?: SearchScopeEnum;
}

export interface JobEstimatorConfirmPaperDTO {
  currentParts: JobEstimatorConfirmPaperDTOPart[];
  industryAverageParts: JobEstimatorConfirmPaperDTOPart[];
  revshopParts?: JobEstimatorConfirmPaperDTOPart[];
}

export interface JobEstimatorConfirmPaperDTOPart {
  skuId: number;
  skuDisplay: string;
  paper: JobEstimatorConfirmPaperDTOPartPaper;
}

export interface JobEstimatorConfirmPaperDTOPartPaper {
  ongoing: JobEstimatorConfirmPaperDTOPartPaperOption;
  options: JobEstimatorConfirmPaperDTOPartPaperOption[];
  basisWeights: string[];
  brands: string[];
  colors: string[];
}

export interface JobEstimatorConfirmPaperDTOPartPaperOption {
  /** Metric twin, sent alongside so switching units needs no refetch. */
  basisWeightMetric?: string;

  /** Metric twin, sent alongside so switching units needs no refetch. */
  widthMm?: number;
  lengthMm?: number;

  equivalentCode: string;
  category: string;
  finish: string;
  basisWeight: string;
  color: string;
  brand: string;
  widthIn: number;
  lengthIn: number;
  price: number;
}

export interface JobEstimatorConfirmPaperUpdateParams {
  skuId: number;
  paperEquivalentCode: string;
  columnType: JobEstimatorConfirmPaperUpdateParamsColumnType;
  quantityItemSerialNumber: number;
}

export enum JobEstimatorConfirmPaperUpdateParamsColumnType {
  CURRENT = 'CURRENT',
  INDUSTRY_AVERAGE = 'INDUSTRY_AVERAGE',
  REVSHOP = 'REVSHOP',
}

export interface SkuDynamicSaveParamsDTO {
  jobConfigId: string;
  skuId: number;
  frontTemplateId: number;
  backendTemplateId?: number;
  saveParams: Record<string, string>;
}

export interface SkuDynamicPreviewResponseDTO {
  jobConfigId: string;
  skuId: number;
  frontPdfUrl: string;
  frontPdfName: string;
  backPdfUrl?: string;
  backPdfName?: string;
  backImageUrl?: string;
}

export interface SkuDynamicApproveResponseDTO {
  jobConfigId: string;
  skuId: string;
  approved: boolean;
  frontPdfUrl?: string;
  backPdfUrl?: string;
  saveParams?: Record<string, string>;
  backImageUrl?: string;
}

export interface RFQSupplierResponseRequestDTO {
  orgId?: number;
  rfqId?: number;
  rfqIds?: number[];
  supplierOrgIds: number[];
  status: RFQOrgStatusEnum[];
  startDate: string;
  endDate: string;
  page?: number;
  size?: number;
}

export interface JobRFQSupplierResponseViewer {
  id: number | null;
  skuName: string | null;
  skuId: number | null;
  projectName: string | null;
  orgId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  customerId: number | null;
  customerFullName: string | null;
  customerOrgId: number | null;
  customerOrgName: string | null;
  rfqStatus: string | null;
  qtyItems: JobRFQSupplierResponseQtyItem[];
}

export interface JobRFQSupplierResponseQtyItem {
  supplierOrgId: number | null;
  supplierOrgName: string | null;
  serialNumber: number;
  rfqOrgStatus: string | null;
  sendAt: string | null;
  responseAt: string | null;
  awardAt: string | null;
  quantity: number | null;
  readyDate: string | null;
  deliverDate: string | null;
  revxFreightCharge: number | null;
  revxFreightCost: number | null;
  revxFreightMarkup: number | null;
  revxPrintingCharge: number | null;
  revxPrintingCost: number | null;
  revxPrintingMarkup: number | null;
  revxCharge: number | null;
  extraSupplierShippingCharge: number | null;
  extraSupplierShippingCost: number | null;
  extraSupplierShippingMarkup: number | null;
  extraSupplierShippingMarkupPct: number | null;
  supplierNote: string | null;
  sourcerNote: string | null;
  supplierFiles: JobConfigFileItem[] | null;
  shippingItems: JobRFQShippingItemViewer[] | null;

  printingCost: number | null;
  supplierPrintingMarkupPct: number | null;
   supplierPrintingMarkup: number | null;
   supplierPrintingCharge: number | null;

   freightCost: number | null;
   supplierShippingMarkupPct: number | null;
   supplierShippingMarkup: number | null;
  supplierFreightCharge: number | null;
  supplierTotalCharge: number | null;

}
